import { CommonModule, DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, LOCALE_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@environment';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { InputComponent } from '@frontendlib/components/controls/input/input.component';
import { AuthService } from '@frontendlib/common/services/auth.service';
import {
  deviceSessionLimitMessage,
  isDeviceSessionLimitRefusal,
} from '@frontendlib/common/services/device-session-limit';
import { DeviceSessionsService } from '@frontendlib/common/services/device-sessions.service';
import { FormValidators } from '@frontendlib/common/forms/FormValidators';
import { getCurrentTimezone } from '@frontendlib/common/utils/get-current-timezone';
import { AVATAR_PALETTE, avatarPalette } from '@sharedlib/common/utils/avatar-palette';
import {
  isValidWorkspaceAddressLocalPart,
  normalizeWorkspaceAddressLocalPart,
} from '@sharedlib/common/utils/workspace-address';
import {
  SpaceInvitationLocalPartAvailability,
  type SpaceInvitationResolutionDto,
  SpaceInvitationState,
} from '@sharedlib/dtos/dto/accounts/invitation-acceptance.dto';
import { SpaceInvitationRefusals } from '@sharedlib/dtos/dto/accounts/space-invitations.dto';
import { EMPTY, timer } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { InvitationAcceptanceApiService } from './invitation-acceptance-api.service';
import { enforceJoinRoutePrivacy } from './join-route-privacy';
import { spaceChatPath } from '@modules/common/utils/space-route';

type PublicStep = 'landing' | 'account' | 'password' | 'loading';
type Availability = 'idle' | 'pending' | 'available' | 'taken' | 'invalid' | 'network';
type InvitationField = 'firstName' | 'lastName' | 'teamEmailLocalPart' | 'password' | 'termsAccepted';

type ValidationEntry = {
  property?: unknown;
  constraints?: unknown;
};

const GENERIC_SIGNUP_ERROR = ['We couldn’t create your account.', 'Please try again.'].join(' ');
const DEVICE_LIMIT_WITHOUT_FIGURE = 'This device is signed in to too many accounts — sign out of one to add another.';

@Component({
  selector: 'app-invitation-acceptance',
  standalone: true,
  imports: [CommonModule, IconComponent, InputComponent, ReactiveFormsModule],
  templateUrl: './invitation-acceptance.component.html',
  styleUrl: './invitation-acceptance.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvitationAcceptanceComponent {
  private readonly api = inject(InvitationAcceptanceApiService);
  private readonly auth = inject(AuthService);
  private readonly deviceSessions = inject(DeviceSessionsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly locale = inject(LOCALE_ID);
  private readonly document = inject(DOCUMENT);

  protected readonly SpaceInvitationState = SpaceInvitationState;
  /** The landing site's Terms of Service; `/auth/terms` is no page and only led to sign-in (#2874). */
  protected readonly termsUrl = `${environment.landingUrl}/terms-of-service`;
  protected readonly avatarPalette = avatarPalette;
  /** The fixed pair used only while the invitation carries no `inviterId` — see {@link inviterAvatarPalette}. */
  protected readonly inviterPalette = AVATAR_PALETTE[6];
  protected readonly resolution = signal<SpaceInvitationResolutionDto | null>(null);
  protected readonly resolving = signal(true);
  protected readonly resolveError = signal<'unknown' | 'network' | 'rate-limit' | null>(null);
  protected readonly step = signal<PublicStep>('landing');
  protected readonly availability = signal<Availability>('idle');
  protected readonly submitError = signal<string | null>(null);
  protected readonly requestPending = signal(false);
  protected readonly requestSent = signal(false);
  protected readonly requestError = signal(false);
  protected readonly alreadyMemberFailure = signal(false);
  protected readonly fieldErrors = signal<Partial<Record<InvitationField, string>>>({});
  private readonly token = this.route.snapshot.paramMap.get('token') ?? '';
  private readonly hadSessionAtLoad = this.auth.isAuthenticated();

  protected readonly detailsForm = new FormGroup({
    firstName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    lastName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });
  protected readonly passwordForm = new FormGroup(
    {
      teamEmailLocalPart: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      password: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, FormValidators.weakPassword],
      }),
      passwordConfirmation: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      termsAccepted: new FormControl(false, { nonNullable: true, validators: [Validators.requiredTrue] }),
    },
    { validators: [FormValidators.passwordConfirmation('password', 'passwordConfirmation')] },
  );

  constructor() {
    const restoreReferrerPolicy = enforceJoinRoutePrivacy(this.document);
    this.destroyRef.onDestroy(restoreReferrerPolicy);

    this.api
      .resolve(this.token)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.resolving.set(false)),
      )
      .subscribe({
        next: (resolution) => {
          this.resolution.set(resolution);
          if (
            resolution.state === SpaceInvitationState.VALID &&
            this.route.snapshot.queryParamMap.get('create') === '1'
          ) {
            this.step.set('account');
          }
        },
        error: (error: unknown) =>
          this.resolveError.set(
            error instanceof HttpErrorResponse && error.status === 404
              ? 'unknown'
              : error instanceof HttpErrorResponse && error.status === 429
                ? 'rate-limit'
                : 'network',
          ),
      });

    this.passwordForm.controls.teamEmailLocalPart.valueChanges
      .pipe(
        map(normalizeWorkspaceAddressLocalPart),
        switchMap((localPart) => {
          this.passwordForm.controls.teamEmailLocalPart.setValue(localPart, { emitEvent: false });
          this.clearFieldError('teamEmailLocalPart');
          if (!isValidWorkspaceAddressLocalPart(localPart)) {
            this.setAvailability(localPart ? 'invalid' : 'idle');
            return EMPTY;
          }
          this.setAvailability('pending');
          return timer(300).pipe(
            switchMap(() => this.api.resolve(this.token, localPart)),
            map((result) => result.localPartAvailability),
            catchError(() => {
              this.setAvailability('network');
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((availability) => {
        this.setAvailability(
          availability === SpaceInvitationLocalPartAvailability.AVAILABLE
            ? 'available'
            : availability === SpaceInvitationLocalPartAvailability.TAKEN
              ? 'taken'
              : 'invalid',
        );
      });

    this.detailsForm.controls.firstName.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.clearFieldError('firstName'));
    this.detailsForm.controls.lastName.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.clearFieldError('lastName'));
    this.passwordForm.controls.password.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.clearFieldError('password'));
    this.passwordForm.controls.termsAccepted.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.clearFieldError('termsAccepted'));
  }

  protected initials(value: string): string {
    return value
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('');
  }

  /**
   * #1274 review F3. `inviterId` (the inviter's public uid) shipped on `SpaceInvitationResolutionDto`
   * after this branch's base (SU-BE-8), so it is read defensively here rather than typed on the DTO —
   * a cast, not `any`, and safe whether or not the field exists on the object at runtime. Once present,
   * the circle keys off the SAME palette the switcher and the owner's notice mail use, so the inviter's
   * colour agrees everywhere; until then it keeps the fixed pair it always had.
   */
  protected inviterAvatarPalette(invitation: SpaceInvitationResolutionDto): (typeof AVATAR_PALETTE)[number] {
    const inviterId = (invitation as SpaceInvitationResolutionDto & { inviterId?: string }).inviterId;
    return inviterId ? avatarPalette(inviterId) : this.inviterPalette;
  }

  protected beginNewAccount(): void {
    this.step.set('account');
  }

  protected backToLanding(): void {
    this.step.set('landing');
  }

  protected continueToPassword(): void {
    this.detailsForm.markAllAsTouched();
    if (this.detailsForm.valid) this.step.set('password');
  }

  protected createAccount(): void {
    this.passwordForm.markAllAsTouched();
    if (this.passwordForm.invalid || this.availability() !== 'available' || this.step() === 'loading') return;
    this.step.set('loading');
    this.submitError.set(null);
    this.alreadyMemberFailure.set(false);
    this.fieldErrors.set({});
    const details = this.detailsForm.getRawValue();
    const account = this.passwordForm.getRawValue();
    this.api
      .signup(this.token, {
        ...details,
        teamEmailLocalPart: account.teamEmailLocalPart,
        password: account.password,
        termsAccepted: true,
        timeZone: getCurrentTimezone(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.auth.setAuthPayload(response.payload);
          const teamId = this.resolution()?.teamId;
          if (!teamId) return;
          if (this.hadSessionAtLoad) {
            this.navigateDocument(spaceChatPath(teamId).join('/'));
            return;
          }
          void this.router.navigate(spaceChatPath(teamId));
        },
        error: (error: unknown) => this.handleSignupError(error),
      });
  }

  protected fieldValidationMessages(field: InvitationField): Record<string, string> {
    const messages: Record<string, string> = {
      required: 'This field is required',
      weakPassword: 'Please choose a stronger password',
      passwordRequirements: 'Password must be at least 8 characters, have mixed case letters and include one number',
      notEqual: 'Passwords must match.',
      unavailable: this.availabilityMessage(),
    };
    const server = this.fieldErrors()[field];
    if (server) messages.server = server;
    return messages;
  }

  protected requestNewInvitation(): void {
    if (this.requestPending() || this.requestSent()) return;
    this.requestPending.set(true);
    this.requestError.set(false);
    this.api
      .requestNew(this.token)
      .pipe(
        finalize(() => this.requestPending.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.requestSent.set(true),
        error: () => this.requestError.set(true),
      });
  }

  protected goToStitch(): void {
    if (this.auth.isAuthenticated()) this.navigateDocument('/mail');
    else this.goToLogin();
  }

  protected acceptedDate(value?: Date): string {
    if (!value) return '';
    const date = new Date(value);
    const now = new Date();
    return new Intl.DateTimeFormat(this.locale, {
      month: 'short',
      day: 'numeric',
      ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
    }).format(date);
  }

  private goToLogin(): void {
    this.navigateDocument('/auth/login');
  }

  private navigateDocument(url: string): void {
    this.document.location.assign(url);
  }

  private handleSignupError(error: unknown): void {
    if (!(error instanceof HttpErrorResponse)) {
      this.step.set('password');
      this.submitError.set(GENERIC_SIGNUP_ERROR);
      return;
    }

    if (error.status === 404) {
      this.resolveError.set('unknown');
      return;
    }

    // Keyed on the CODE: every other 422 keeps the sentence it ships today. The refusal comes before
    // any account exists, so nothing was created and the person can simply try again.
    if (isDeviceSessionLimitRefusal(error)) {
      this.showDeviceSessionLimit();
      return;
    }

    if (error.status === 400 && this.applyValidationErrors(error.error)) return;

    const message = this.errorMessage(error.error);
    if (error.status === 409) {
      if (message === SpaceInvitationRefusals.INVITATION_EXPIRED) {
        this.showDeadState(SpaceInvitationState.EXPIRED);
        return;
      }
      if (message === SpaceInvitationRefusals.INVITATION_REVOKED) {
        this.showDeadState(SpaceInvitationState.REVOKED);
        return;
      }
      if (message === SpaceInvitationRefusals.INVITATION_ALREADY_ACCEPTED) {
        this.showDeadState(SpaceInvitationState.ALREADY_ACCEPTED);
        return;
      }
      if (message === SpaceInvitationRefusals.ALREADY_MEMBER) {
        this.alreadyMemberFailure.set(true);
        this.step.set('landing');
        return;
      }
      if (
        message === SpaceInvitationRefusals.LOCAL_PART_TAKEN ||
        message === SpaceInvitationRefusals.LOCAL_PART_RESERVED
      ) {
        this.step.set('password');
        this.setAvailability('taken');
        this.focusAddress();
        return;
      }
      if (message === SpaceInvitationRefusals.LOCAL_PART_INVALID) {
        this.step.set('password');
        this.setAvailability('invalid');
        this.focusAddress();
        return;
      }
    }

    this.step.set('password');
    this.submitError.set(GENERIC_SIGNUP_ERROR);
  }

  /**
   * Say why, and what to do: this browser already holds as many accounts as it may.
   *
   * Name and address stay typed so a retry after signing out elsewhere needs no retyping; the
   * password is cleared and never kept. The figure comes from the server (the same read the account
   * switcher uses), never from a number written here.
   */
  private showDeviceSessionLimit(): void {
    this.step.set('password');
    this.passwordForm.patchValue({ password: '', passwordConfirmation: '' });
    this.deviceSessions
      .resolveLimit()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (limit) => this.submitError.set(deviceSessionLimitMessage(limit)),
        // The reason must not be lost if the figure cannot be read.
        error: () => this.submitError.set(DEVICE_LIMIT_WITHOUT_FIGURE),
      });
  }

  private showDeadState(state: SpaceInvitationState): void {
    const invitation = this.resolution();
    if (invitation) this.resolution.set({ ...invitation, state });
    this.step.set('landing');
  }

  private errorMessage(body: unknown): string | null {
    if (typeof body === 'string') return body;
    if (!body || typeof body !== 'object') return null;
    const message = Reflect.get(body, 'message') as unknown;
    return typeof message === 'string' ? message : null;
  }

  private applyValidationErrors(body: unknown): boolean {
    const nestedMessage = body && typeof body === 'object' ? (Reflect.get(body, 'message') as unknown) : undefined;
    const entries = Array.isArray(body) ? body : Array.isArray(nestedMessage) ? nestedMessage : null;
    if (!entries) return false;

    const errors: Partial<Record<InvitationField, string>> = {};
    for (const entry of entries) {
      if (typeof entry === 'string') {
        const field = this.fieldFromMessage(entry);
        if (field) errors[field] = entry;
        continue;
      }
      if (!entry || typeof entry !== 'object') continue;
      const validation = entry as ValidationEntry;
      if (!this.isInvitationField(validation.property)) continue;
      const constraints = validation.constraints;
      if (!constraints || typeof constraints !== 'object') continue;
      const message = Object.values(constraints as Record<string, unknown>).find(
        (value): value is string => typeof value === 'string',
      );
      if (message) errors[validation.property] = message;
    }

    if (!Object.keys(errors).length) return false;
    this.fieldErrors.set(errors);
    this.applyControlServerErrors(errors);
    this.step.set(errors.firstName || errors.lastName ? 'account' : 'password');
    return true;
  }

  private applyControlServerErrors(errors: Partial<Record<InvitationField, string>>): void {
    for (const field of Object.keys(errors) as InvitationField[]) {
      const control =
        field === 'firstName' || field === 'lastName'
          ? this.detailsForm.controls[field]
          : field === 'teamEmailLocalPart'
            ? this.passwordForm.controls.teamEmailLocalPart
            : field === 'password'
              ? this.passwordForm.controls.password
              : this.passwordForm.controls.termsAccepted;
      control.setErrors({ ...(control.errors ?? {}), server: true });
      control.markAsTouched();
    }
  }

  private fieldFromMessage(message: string): InvitationField | null {
    const normalized = message.toLowerCase();
    if (normalized.includes('firstname') || normalized.includes('first name')) return 'firstName';
    if (normalized.includes('lastname') || normalized.includes('last name')) return 'lastName';
    if (normalized.includes('teamemaillocalpart') || normalized.includes('email')) return 'teamEmailLocalPart';
    if (normalized.includes('password')) return 'password';
    if (normalized.includes('termsaccepted') || normalized.includes('terms')) return 'termsAccepted';
    return null;
  }

  private isInvitationField(value: unknown): value is InvitationField {
    return (
      value === 'firstName' ||
      value === 'lastName' ||
      value === 'teamEmailLocalPart' ||
      value === 'password' ||
      value === 'termsAccepted'
    );
  }

  private setAvailability(availability: Availability): void {
    this.availability.set(availability);
    const control = this.passwordForm.controls.teamEmailLocalPart;
    const errors = { ...(control.errors ?? {}) };
    delete errors.unavailable;
    if (availability === 'taken' || availability === 'invalid' || availability === 'network') {
      errors.unavailable = true;
      control.markAsTouched();
    }
    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  private availabilityMessage(): string {
    if (this.availability() === 'taken') return 'This email address is taken. Please choose a different one.';
    if (this.availability() === 'invalid') return 'Please enter a valid email address.';
    if (this.availability() === 'network') return 'We couldn’t check this address. Please try again.';
    return '';
  }

  private clearFieldError(field: InvitationField): void {
    if (!this.fieldErrors()[field]) return;
    const next = { ...this.fieldErrors() };
    delete next[field];
    this.fieldErrors.set(next);
    const control =
      field === 'firstName' || field === 'lastName'
        ? this.detailsForm.controls[field]
        : field === 'teamEmailLocalPart'
          ? this.passwordForm.controls.teamEmailLocalPart
          : field === 'password'
            ? this.passwordForm.controls.password
            : this.passwordForm.controls.termsAccepted;
    const errors = { ...(control.errors ?? {}) };
    delete errors.server;
    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  private focusAddress(): void {
    queueMicrotask(() => this.document.getElementById('team-email-local-part')?.focus());
  }
}
